import { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export type ChipSize = 'sm' | 'md';

export interface ChipProps {
  label: ReactNode;
  /** When provided, renders a remove (×) button. */
  onRemove?: () => void;
  /** Accessible label for the remove button (e.g. "Remove Has docs"). */
  removeLabel?: string;
  size?: ChipSize;
  isDisabled?: boolean;
  className?: string;
}

const sizing: Record<
  ChipSize,
  { box: string; pad: (r: boolean) => string; icon: 'xs' | 'sm' }
> = {
  sm: {
    box: 'h-6 text-[13px]',
    pad: r => (r ? 'pl-2 pr-1' : 'px-2'),
    icon: 'xs',
  },
  md: { box: 'h-7', pad: r => (r ? 'pl-2.5 pr-1' : 'px-2.5'), icon: 'xs' },
};

/**
 * Chip — a removable token (a selected value). Distinct from Badge (a static
 * status label): a Chip can be dismissed, used inside multi-select inputs and
 * filter bars. A soft filled token with a real remove hit-area. Foundation +
 * Icon primitive.
 */
export const Chip = ({
  label,
  onRemove,
  removeLabel,
  size = 'md',
  isDisabled,
  className,
}: ChipProps) => {
  const t = useT();
  const s = sizing[size];
  return (
    <span
      className={cx(
        typography.label,
        'inline-flex items-center gap-1 rounded-md bg-muted-strong text-fg-secondary',
        s.box,
        s.pad(Boolean(onRemove)),
        className,
      )}
    >
      <span className="truncate">{label}</span>
      {onRemove && (
        <IconButton
          aria-label={removeLabel ?? t('ui.chip.removeAriaLabel')}
          size="xs"
          variant="ghost"
          isDisabled={isDisabled}
          onPress={onRemove}
          className="-mr-0.5 hover:bg-neutral-hover"
        >
          <Icon as={X} size={s.icon} strokeWidth={2.25} />
        </IconButton>
      )}
    </span>
  );
};
